import { oktaAuth } from "../../auth/services/oktaAuth";
import { apiUrl } from "../../shared/services/service";
import type {
  AdminFlowDefinition,
  AdminFlowRun,
  AdminFlowRunSummary,
  AdminFlowTool,
  AdminFlowSavedDraft,
} from "../models/admin-flows.models";
import { getAdminAgentBrowserTokenScope } from "../utils/admin-agent.utils";
import axios from "axios";

const api = axios.create();
const endpoint = `${apiUrl.replace(/\/+$/, "")}/admin/agent`;

// Flow content uses the same rendered-session binding as private assistant chats.
// Never let the shared API client's cached bearer expose a previous administrator's run evidence.
const flowRequestOptions = (expectedToken: string | undefined, signal?: AbortSignal) => {
  const token = oktaAuth.getAccessToken();

  if (
    !token ||
    !expectedToken ||
    getAdminAgentBrowserTokenScope(token, true) !== getAdminAgentBrowserTokenScope(expectedToken)
  ) {
    throw new Error("Your sign-in changed. Sign in again before opening AI flows.");
  }

  return { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, signal };
};

export const listAdminFlowTools = async (token: string | undefined, signal?: AbortSignal): Promise<AdminFlowTool[]> => {
  const response = await api.get<{ tools: AdminFlowTool[] }>(
    `${endpoint}/flows/tools`,
    flowRequestOptions(token, signal),
  );

  return response.data.tools;
};

export const listAdminFlowRuns = async (
  token: string | undefined,
  signal?: AbortSignal,
): Promise<AdminFlowRunSummary[]> => {
  const response = await api.get<{ runs: AdminFlowRunSummary[] }>(
    `${endpoint}/flows/runs`,
    flowRequestOptions(token, signal),
  );

  return response.data.runs;
};

export const listSavedAdminFlowDrafts = async (
  token: string | undefined,
  signal?: AbortSignal,
): Promise<{ sqlEnabled: boolean; drafts: AdminFlowSavedDraft[] }> => {
  const response = await api.get<{ sqlEnabled: boolean; drafts: AdminFlowSavedDraft[] }>(
    `${endpoint}/flows/drafts`,
    flowRequestOptions(token, signal),
  );

  return response.data;
};

export const saveAdminFlowDraft = async (
  token: string | undefined,
  draft: AdminFlowSavedDraft,
  signal?: AbortSignal,
): Promise<AdminFlowSavedDraft> => {
  const response = await api.put<AdminFlowSavedDraft>(
    `${endpoint}/flows/drafts/${encodeURIComponent(draft.flow.id)}`,
    draft,
    flowRequestOptions(token, signal),
  );

  return response.data;
};

export const deleteSavedAdminFlowDraft = async (token: string | undefined, id: string): Promise<void> => {
  await api.delete(`${endpoint}/flows/drafts/${encodeURIComponent(id)}`, flowRequestOptions(token));
};

export const getAdminFlowRun = async (
  token: string | undefined,
  id: string,
  signal?: AbortSignal,
): Promise<AdminFlowRun> => {
  const response = await api.get<AdminFlowRun>(
    `${endpoint}/flows/runs/${encodeURIComponent(id)}`,
    flowRequestOptions(token, signal),
  );

  return response.data;
};

export const startAdminFlowRun = async (
  token: string | undefined,
  flow: AdminFlowDefinition,
  requestId: string,
): Promise<AdminFlowRun> => {
  const response = await api.post<AdminFlowRun>(
    `${endpoint}/flows/runs`,
    { flow, requestId },
    flowRequestOptions(token),
  );

  return response.data;
};

export const stopAdminFlowRun = async (token: string | undefined, id: string): Promise<AdminFlowRun> => {
  const response = await api.post<AdminFlowRun>(
    `${endpoint}/flows/runs/${encodeURIComponent(id)}/stop`,
    {},
    flowRequestOptions(token),
  );

  return response.data;
};

export const proposeAdminFlow = async (
  token: string | undefined,
  flow: AdminFlowDefinition,
  message: string,
): Promise<{ flow: AdminFlowDefinition; explanation: string }> => {
  const response = await api.post<{ flow: AdminFlowDefinition; explanation: string }>(
    `${endpoint}/flows/draft`,
    { flow, message },
    { ...flowRequestOptions(token), timeout: 120000 },
  );

  return response.data;
};
